// The persistent left rail: fixed reassurance copy plus the vertical's
// process-flow illustration.
//
// Config-driven end to end — greeting, title, accent, subtitle, bullets and
// image all come from the journey config, so the twelve hand-copied versions
// of this panel that used to live inside each page collapse into this one.
import React from "react";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { motion, useReducedMotion } from "framer-motion";

import ResponsiveImage from "../elements/ResponsiveImage";
const MotionBox = motion.create(Box);

const DEFAULT_FOOTNOTE = {
  title: "Free to compare",
  text: "Pay your insurer directly — no SmartSure fees",
};
/**
 * The left rail: what this vertical is, and why it's worth finishing.
 *
 * It deliberately does *not* repeat the journey's steps. It used to, and having
 * a step list here as well as in the header meant two progress indicators
 * fighting for the same job, pushing the form down the page and making every
 * screen scroll further than it needed to. Progress lives in the ribbon; the
 * rail is for reassurance and context.
 *
 * @param {Object} props
 * @param {import("../../form/types").RailConfig} props.rail
 * @param {boolean} [props.compact]  On a short viewport, drop the bullet list.
 *   For a step whose own body is designed to fit one screen: the rail was the
 *   only thing making those steps scroll on a 768px-tall laptop.
 */
const JourneyRail = ({ rail, compact = false }) => {
  const reduceMotion = useReducedMotion();
  if (!rail) return null;
  const bullets = rail.bullets || [];
  const bulletsTitle = rail.bulletsTitle || "SmartSure provides";
  const footnote =
    rail.footnote === undefined ? DEFAULT_FOOTNOTE : rail.footnote;

  return (
    <Paper
      elevation={0}
      component="aside"
      sx={{
        position: "sticky",
        top: 180,
        p: 4.5,
        borderRadius: 4,
        background: "linear-gradient(180deg, #fdf6ea 0%, #f7eada 100%)",
        border: "1px solid rgba(201,167,119,0.18)",
        boxShadow: "0 14px 32px rgba(123,110,102,0.10)",
        overflow: "hidden",
        "& > *:not(.rail-watermark)": { position: "relative", zIndex: 1 },
      }}
    >
      {rail.greeting && (
        <Typography variant="h4" sx={headingSx}>
          {rail.greeting}
        </Typography>
      )}
      {rail.title && (
        <Typography variant="h4" sx={headingSx}>
          {rail.title}{" "}
          {rail.accent && (
            <Box component="span" sx={{ color: "#C9A777" }}>
              {rail.accent}
            </Box>
          )}
        </Typography>
      )}
      {rail.subtitle && (
        <Typography
          sx={{
            color: "#7B6E66",
            mt: 1.5,
            fontSize: "0.9375rem",
            lineHeight: 1.6,
          }}
        >
          {rail.subtitle}
        </Typography>
      )}

      {/* The vertical's illustration, as a corner watermark.
          Absolutely positioned on purpose: rendered in flow it was a 180px
          block that pushed the reassurance copy down and made every form page
          scroll for no reason. Out of flow it costs no height at all, and the
          copy simply runs past it.

          The drift is the floating effect these illustrations used to have —
          4s, 6px, and switched off for anyone who asks for reduced motion. */}
      {rail.image && (
        <MotionBox
          className="rail-watermark"
          aria-hidden
          animate={reduceMotion ? undefined : { y: [0, -6, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute",
            right: 12,
            bottom: 12,
            width: { tablet: 104, lg: 132 },
            height: { tablet: 104, lg: 132 },
            opacity: 0.38,
            pointerEvents: "none",
            // Behind the copy, so where they do overlap it reads as a
            // watermark rather than a collision. Everything else in the rail
            // is lifted to zIndex 1 below.
            zIndex: 0,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            "& img": {
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            },
          }}
        >
          <ResponsiveImage src={rail.image} alt="" sizes="132px" />
        </MotionBox>
      )}

      {bullets.length > 0 && (
        <Box
          sx={{
            mt: 3,
            ...(compact && { "@media (max-height: 860px)": { display: "none" } }),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: "#a1885e",
              fontWeight: 700,
              letterSpacing: 1.6,
              fontSize: "0.72rem",
            }}
          >
            {bulletsTitle}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {bullets.map((text) => (
              <Stack
                key={text}
                direction="row"
                alignItems="center"
                spacing={1.25}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 2px 6px rgba(123,110,102,0.10)",
                  }}
                >
                  <CheckCircleOutlineIcon
                    sx={{ color: "#a1885e", fontSize: 16 }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: "#3d322b",
                    fontWeight: 500,
                    fontSize: "0.9375rem",
                  }}
                >
                  {text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {footnote && (
        <>
          <Divider sx={{ my: 3, borderColor: "rgba(201,167,119,0.22)" }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ pr: { tablet: 9, lg: 11 } }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "rgba(201,167,119,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LocalOfferOutlinedIcon sx={{ color: "#a1885e", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#3d322b",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                }}
              >
                {footnote.title}
              </Typography>
              <Typography
                sx={{
                  color: "#7B6E66",
                  fontSize: "0.8125rem",
                  lineHeight: 1.3,
                }}
              >
                {footnote.text}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </Paper>
  );
};

const headingSx = {
  fontWeight: 700,
  color: "#3d322b",
  lineHeight: 1.1,
  letterSpacing: "-0.02em",
};

export default JourneyRail;
